import { useMutation } from '@tanstack/react-query'
import { App, Button, Flex, Form, Space, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { RotateCcw, Save as SaveIcon } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ChecklistItem } from 'src/analytics'
import {
  organizationsRolesListQueryKey,
  organizationsRolesPartialUpdateMutation,
  permissionsListQueryKey,
  type OrganizationRoleFull,
  type PermissionsLocalized
} from 'src/api/main'
import { ProTableContainer } from 'src/components'
import { useReportJourneyStep } from 'src/features/workspaces/onboarding'
import { useInvalidatePredicateQueries } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'

import { type RolesFormValues, usePermissionsForm } from './hooks'
import { RoleColumnHeader } from './RoleColumnHeader'
import { RolePermissionCell } from './RolePermissionCell'
import type { MatrixRow } from './types'
import { getGroupedPermissions } from './utils'

const { Text } = Typography

type Props = {
  groupedPermissions: Record<string, PermissionsLocalized[]>
  roles: OrganizationRoleFull[]
  applicationType?: string
  applicationId?: number
  organizationId: number
  onEditRole: (role: OrganizationRoleFull) => void
  onDeleteRole: (role: OrganizationRoleFull) => void
  moduleOrder?: readonly string[]
  moduleLabels?: Record<string, string>
}

export const RolesPermissionsTable = ({
  groupedPermissions,
  roles,
  applicationType: _applicationType,
  onEditRole,
  onDeleteRole,
  moduleOrder,
  moduleLabels
}: Props) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const reportStep = useReportJourneyStep()

  const { mutateAsync: updateRole, isPending: isUpdating } = useMutation({
    ...organizationsRolesPartialUpdateMutation()
  })

  const { form, initialValues } = usePermissionsForm(roles, groupedPermissions)
  const formValues = Form.useWatch('roles', form) || {}

  useEffect(() => {
    form.setFieldsValue(initialValues)
  }, [form, initialValues])

  const tableData: MatrixRow[] = getGroupedPermissions(groupedPermissions, {
    moduleOrder,
    moduleLabels
  })

  const onSelectAllForRole = (roleId: number, checked: boolean) => {
    const newRoleValues: { [moduleResource: string]: number[] } = {}
    Object.entries(groupedPermissions).forEach(([module, permissions]) => {
      newRoleValues[module] = checked ? permissions.map((p) => p.id) : []
    })
    form.setFieldValue(['roles', roleId], newRoleValues)
    form.setFields(
      Object.keys(groupedPermissions).map((module) => ({
        name: ['roles', roleId, module],
        touched: true
      }))
    )
  }

  const buildRoleColumn = (role: OrganizationRoleFull) => ({
    title: (
      <RoleColumnHeader
        role={role}
        onEdit={onEditRole}
        onDelete={onDeleteRole}
        onSelectAll={
          role.is_admin ? undefined : () => onSelectAllForRole(role.id, true)
        }
        onDeselectAll={
          role.is_admin ? undefined : () => onSelectAllForRole(role.id, false)
        }
      />
    ),
    key: `role-${role.id}`,
    width: 460,
    render: (
      _: unknown,
      record: { key: string; permissions: PermissionsLocalized[] }
    ) => {
      const currentValue: number[] = formValues?.[role.id]?.[record.key] ?? []

      return (
        <Form.Item
          name={['roles', role.id, record.key]}
          style={{ marginBottom: 0 }}
        >
          <RolePermissionCell
            role={role}
            permissions={record.permissions}
            value={currentValue}
            onChange={(value) =>
              form.setFieldValue(['roles', role.id, record.key], [...value])
            }
          />
        </Form.Item>
      )
    }
  })

  const columns: ColumnsType<MatrixRow> = [
    {
      title: t('workspace.apps.roles.module'),
      dataIndex: 'module',
      key: 'module',
      width: 280,
      fixed: 'left' as const,
      render: (text: string) => <Text strong>{text}</Text>
    },
    ...roles.map(buildRoleColumn)
  ]

  const onSave = async () => {
    try {
      const values = (await form.validateFields()) as RolesFormValues

      const tasks = Object.entries(values.roles ?? {})
        .map(([roleId, modules]) => {
          const role = roles.find((r) => r.id === Number(roleId))
          if (!role || role.is_admin) return null
          const permissionIds = Object.values(modules).flat() as number[]

          return updateRole({
            path: { id: Number(roleId) },
            body: {
              name: role.name,
              description: role.description ?? '',
              permissions_ids: permissionIds
            }
          })
        })
        .filter(Boolean)

      await Promise.all(tasks)
      await invalidateQueries([
        organizationsRolesListQueryKey(),
        permissionsListQueryKey()
      ])
      reportStep(ChecklistItem.ROLES)
      message.success(t('workspace.apps.roles.permissionsUpdated'))
    } catch (error) {
      showApiErrors(error)
    }
  }

  return (
    <Form form={form} initialValues={initialValues}>
      <ProTableContainer<MatrixRow>
        options={false}
        columnsReorderable={false}
        showSearch={false}
        rowKey="key"
        bordered
        dataSource={tableData}
        columns={columns}
        pagination={false}
        footer={() => (
          <Flex justify="space-between" align="center">
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.apps.roles.matrixHint')}
            </Text>
            <Space>
              <Button
                icon={<RotateCcw size={14} />}
                onClick={() => form.resetFields()}
              >
                {t('workspace.apps.roles.resetToDefault')}
              </Button>
              <Button
                type="primary"
                icon={<SaveIcon size={14} />}
                loading={isUpdating}
                onClick={onSave}
                data-testid="permissions-save"
              >
                {t('save')}
              </Button>
            </Space>
          </Flex>
        )}
      />
    </Form>
  )
}
